import { useState, useEffect, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { BRAND } from "@/lib/constants";
import EditableCell from "@/components/shared/EditableCell";
import { formatDate } from "./DocumentStats";
import { DOC_TYPE_OPTIONS } from "@/constants/canonical";
import api from "@/lib/api";
import { toast } from "sonner";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

// Fields that are routed through the override namespace
// (PATCH/DELETE /api/documents/{id}/overrides/{field}) instead of the
// legacy /metadata write path. Keeps the scraper-derived flat field clean
// so ledger ingest stays explicit.
const OVERRIDE_FIELDS = new Set(["member_name", "doc_type", "fund_name"]);

const FIELD_LABELS = {
  doc_type: "Document Type",
  fund_name: "Fund Name",
  member_name: "Member / Investor",
  notice_date: "Document Date",
  currency: "Currency",
  cash_flow_amount: "Amount",
  nav: "NAV",
  nav_per_share: "NAV Per Share",
  shares: "Shares",
  commitment: "Commitment",
  total_commitment: "Total Commitment",
  paid_in_capital: "Paid-in Capital",
  contributed: "Contributed",
  unfunded_commitment: "Unfunded",
  total_distributions: "Total Distributions",
  valuation_date: "Valuation Date",
  due_date: "Due Date",
  moic: "MOIC",
};

const CURRENCY_OPTIONS = ["AUD", "CAD", "CHF", "EUR", "GBP", "ILS", "JPY", "USD"];

// member_name + doc_type editConfig is computed at render time so the
// dropdown reflects the live canonical/fallback member list.
const STATIC_EDITABLE_FIELDS = {
  fund_name: { type: "text" },
  notice_date: { type: "date" },
  currency: { type: "select", options: CURRENCY_OPTIONS },
  cash_flow_amount: { type: "number" },
  nav: { type: "number" },
  nav_per_share: { type: "number" },
  shares: { type: "number" },
  commitment: { type: "number" },
  total_commitment: { type: "number" },
  paid_in_capital: { type: "number" },
  contributed: { type: "number" },
  unfunded_commitment: { type: "number" },
  total_distributions: { type: "number" },
  valuation_date: { type: "date" },
  due_date: { type: "date" },
  moic: { type: "text" },
};

// Identity / attribution fields — ALWAYS shown (every document has these).
const METADATA_FIELDS = ["doc_type", "fund_name", "member_name", "notice_date", "currency"];

// Extraction (DI-derived) fields — shown only when the doc has extraction data.
const EXTRACTION_FIELDS = [
  "cash_flow_amount", "nav", "nav_per_share", "shares", "commitment",
  "total_commitment", "paid_in_capital", "contributed", "unfunded_commitment",
  "total_distributions", "valuation_date", "due_date", "moic",
];

// A document "has extraction data" when DI extraction ran (ai_extracted), a
// multi-fund breakdown exists, or any extracted field carries a value. Metadata
// is always shown; this only gates the extra "Extracted data" / "Scraper raw
// data" block. (Going forward only ledger-eligible docs are extracted, so
// presence is the only signal the UI needs.)
const hasExtractionData = (d) =>
  d?.ai_extracted === true ||
  (d?.fund_count || 0) > 0 ||
  EXTRACTION_FIELDS.some((f) => d?.[f] !== undefined && d?.[f] !== null && d?.[f] !== "");

// Fund row columns to display
const FUND_ROW_FIELDS = [
  { key: "fund_name", label: "Fund" },
  { key: "commitment", label: "Commitment" },
  { key: "paid_in_capital", label: "Contributed" },
  { key: "total_distributions", label: "Total Distributions" },
  { key: "investment_date", label: "Investment Date" },
];

// Canonical member list is tenant-scoped and rarely changes: one fetch per
// page load, shared by every panel instance (promise cached in module scope).
let canonicalMembersPromise = null;
function loadCanonicalMembers() {
  if (!canonicalMembersPromise) {
    canonicalMembersPromise = Promise.resolve(api.get("/portfolio/v2/canonical-members"))
      .then((r) => (Array.isArray(r?.data?.members) ? r.data.members : []))
      .catch(() => {
        canonicalMembersPromise = null; // allow a retry on next mount
        return [];
      });
  }
  return canonicalMembersPromise;
}

/**
 * Document detail body (metadata, extraction, provenance, fund breakdown).
 * Owns saving: override fields -> /overrides/{field}, others -> /metadata.
 * Props: blobName, onDocLoaded?(document), onFieldSaved?(blob, field, value),
 * fallbackMemberNames? (used only when the canonical list is empty),
 * seedDoc? (shown if the detail fetch fails).
 */
export default function DocumentDetailPanel({
  blobName,
  onDocLoaded,
  onFieldSaved,
  fallbackMemberNames = [],
  seedDoc = null,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [canonicalMembers, setCanonicalMembers] = useState([]);

  useEffect(() => {
    let alive = true;
    loadCanonicalMembers().then((m) => alive && setCanonicalMembers(m));
    return () => { alive = false; };
  }, []);

  const editableFields = useMemo(() => {
    const canonicalNames = (canonicalMembers || [])
      .map((m) => m.canonical_name)
      .filter(Boolean);
    const memberOptions = canonicalNames.length > 0
      ? canonicalNames
      : Array.from(new Set((fallbackMemberNames || []).filter(Boolean))).sort();
    return {
      ...STATIC_EDITABLE_FIELDS,
      doc_type: { type: "select", options: DOC_TYPE_OPTIONS, allowOther: true },
      member_name: { type: "select", options: memberOptions, allowOther: true },
    };
  }, [canonicalMembers, fallbackMemberNames]);

  useEffect(() => {
    if (!blobName) {
      setDetail(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    (async () => api.get(`/documents/detail/${encodeURIComponent(blobName)}`))()
      .then((res) => {
        if (!alive) return;
        setDetail(res.data);
        onDocLoaded?.(res.data?.document);
      })
      .catch((err) => {
        if (!alive) return;
        console.error("Failed to load document detail:", err?.message);
        setDetail({ document: seedDoc || { blob_name: blobName }, fund_rows: [] });
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobName]);

  const docData = detail?.document || seedDoc || { blob_name: blobName };
  const fundRows = detail?.fund_rows || [];
  const extractionPresent = hasExtractionData(docData) || fundRows.length > 0;

  // NOTE: blob names contain slashes; the routes are `{doc_id:path}` which
  // need LITERAL slashes (%2F breaks the match).
  const handleFieldSave = async (fieldName, value) => {
    try {
      if (OVERRIDE_FIELDS.has(fieldName)) {
        const path = `/documents/${blobName}/overrides/${fieldName}`;
        if (value === "" || value == null) {
          await api.delete(path);
        } else {
          await api.patch(path, { value });
        }
      } else {
        await api.patch(`/documents/${blobName}/metadata`, { [fieldName]: value });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to update";
      toast.error(msg);
      throw err; // EditableCell reverts
    }
    toast.success("Updated");
    setDetail((prev) => prev && ({
      ...prev,
      document: { ...prev.document, [fieldName]: value },
    }));
    onFieldSaved?.(blobName, fieldName, value);
  };

  // One editable/read-only field row — shared by the always-on metadata list
  // and the "Extracted data" dropdown.
  const renderFieldRow = (field) => {
    const value = docData[field];
    const label = FIELD_LABELS[field] || field;
    const editConfig = editableFields[field];
    if (!value && !editConfig) return null; // skip empty non-editable fields
    return (
      <div key={field} className="flex items-start py-1.5 border-b border-gray-50">
        <span className="text-[10px] text-gray-400 w-[120px] flex-shrink-0 pt-0.5">
          {label}
        </span>
        <div className="flex-1 text-xs">
          {editConfig ? (
            <EditableCell
              value={
                field.includes("date") && value
                  ? (() => {
                      try { return new Date(value).toISOString().slice(0, 10); }
                      catch { return value; }
                    })()
                  : value || ""
              }
              type={editConfig.type}
              options={editConfig.options}
              allowOther={editConfig.allowOther}
              formatDisplay={field.includes("date") ? (v) => formatDate(v) : null}
              onSave={(val) => handleFieldSave(field, val)}
            />
          ) : (
            <span className="text-gray-700">
              {typeof value === "number" ? value.toLocaleString("en-US") : value || "—"}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <div className="px-4 py-3 border-b flex items-center gap-2 flex-wrap text-sm font-semibold">
        <span className="truncate max-w-[260px]" dir="auto">
          {docData.display_name || docData.original_filename || docData.blob_name}
        </span>
        <Badge
          variant="outline"
          className="text-[10px] capitalize"
          style={{ backgroundColor: BRAND.soft, color: BRAND.text, borderColor: "transparent" }}
        >
          {docData.portal}
        </Badge>
        {docData.fund_count > 0 && (
          <Badge variant="secondary" className="text-[10px]">
            {docData.fund_count} funds
          </Badge>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-6 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : (
          <>
        {/* Metadata — always shown */}
        <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Metadata</h3>
        <div className="space-y-1">
          {METADATA_FIELDS.map(renderFieldRow)}
        </div>

        {/* Extraction data — only when the document actually has it */}
        {extractionPresent && (
          <div className="mt-5">
            {/* Extraction diagnostics / provenance */}
            <p className="text-[10px] text-gray-400 mb-3">
              Extracted by {docData.v19_canonical?.model_id || docData.model_version || "V19"} model
              {docData.enriched_at ? ` · ${formatDate(docData.enriched_at)}` : ""}
            </p>

            {/* Extracted data — collapsed by default */}
            <Collapsible className="mb-4">
              <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase hover:text-gray-700 [&[data-state=open]>svg]:rotate-180">
                <ChevronDown className="w-3 h-3 transition-transform duration-200" />
                Extracted data
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-1 mt-2">
                  {EXTRACTION_FIELDS.map(renderFieldRow)}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Scraper raw data — collapsed by default */}
            {docData.scraper_raw &&
              typeof docData.scraper_raw === "object" &&
              Object.keys(docData.scraper_raw).length > 0 && (
                <Collapsible className="mb-4">
                  <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase hover:text-gray-700 [&[data-state=open]>svg]:rotate-180">
                    <ChevronDown className="w-3 h-3 transition-transform duration-200" />
                    Scraper raw data
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <pre className="mt-2 max-h-64 overflow-auto text-[10px] bg-gray-50 rounded p-2 text-gray-600 whitespace-pre-wrap break-all">
                      {JSON.stringify(docData.scraper_raw, null, 2)}
                    </pre>
                  </CollapsibleContent>
                </Collapsible>
              )}

            {/* Fund Rows (multi-fund docs) */}
            {fundRows.length > 0 && (
              <div className="mt-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                  Fund Breakdown ({fundRows.length} funds)
                </h3>
                <div className="space-y-3">
                  {fundRows.map((row, i) => (
                    <div
                      key={i}
                      className="bg-gray-50 rounded-lg p-3 border border-gray-100"
                    >
                      <p className="text-xs font-medium text-gray-800 mb-2">
                        {row.fund_name || `Fund ${i + 1}`}
                      </p>
                      <div className="grid grid-cols-2 gap-1">
                        {FUND_ROW_FIELDS.filter((f) => f.key !== "fund_name").map((f) => {
                          const val = row[f.key] || row[f.key.replace("paid_in_capital", "contributed")];
                          if (!val) return null;
                          return (
                            <div key={f.key} className="text-[10px]">
                              <span className="text-gray-400">{f.label}: </span>
                              <span className="text-gray-700">
                                {typeof val === "number" ? val.toLocaleString("en-US") : val}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
